import type { ProductVariant } from '../types/pz';
import { formatCatalogProductDimensions } from './productDimensions';
import { sanitizeProductDisplayName } from './productDisplayNameSanitizer';

export { sanitizeProductDisplayName } from './productDisplayNameSanitizer';

/**
 * Product selectors are an operator-facing surface. Technical product codes,
 * SKUs and compatibility ids may be searchable, but must never become the
 * visible fallback label.
 */
export const getProductSelectorName = (
  variant: ProductVariant | undefined,
  unnamedProductLabel: string,
) => {
  if (!variant) return unnamedProductLabel;
  const product = variant.product;
  const technicalValues = [
    variant.id,
    variant.productId,
    variant.sku,
    variant.ean,
    product?.id,
    product?.code,
    product?.ean,
  ];

  for (const candidate of [product?.name, product?.englishName, variant.variantName]) {
    const displayName = sanitizeProductDisplayName(candidate, technicalValues);
    if (displayName) return displayName;
  }

  return unnamedProductLabel;
};

export const getProductSelectorDimensions = (variant?: ProductVariant) => (
  variant ? formatCatalogProductDimensions(variant.product, variant) : ''
);

export const getProductSelectorEan = (variant?: ProductVariant) => (
  variant?.ean || variant?.product?.ean || ''
);

export const isRealProductVariant = (variant?: ProductVariant) => {
  if (!variant) return false;
  if (variant.isDefault !== undefined) return variant.isDefault === false;
  return Boolean(variant.variantName && variant.variantName !== 'Standard');
};

type ProductVariantAttributeSource = Pick<ProductVariant, 'attributes'>;

export const getProductVariantAttributeEntries = (variant?: ProductVariantAttributeSource) => (
  Object.entries(variant?.attributes || {})
    .map(([key, value]) => [String(key).trim(), String(value).trim()] as const)
    .filter(([key, value]) => Boolean(key && value))
    .sort(([left], [right]) => left.localeCompare(right, 'pl'))
);

export const getProductVariantName = (variant?: ProductVariant) => {
  if (!isRealProductVariant(variant)) return '';

  const attributeLabel = getProductVariantAttributeEntries(variant)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');

  return attributeLabel || String(variant?.variantName || '').trim();
};

export const getProductVariantDisplayName = (
  variant: ProductVariant | undefined,
  basicProductLabel: string,
) => (
  isRealProductVariant(variant)
    ? getProductVariantName(variant)
    : basicProductLabel
);

export const getProductSelectionLabel = (
  variant: ProductVariant | undefined,
  unnamedProductLabel: string,
) => {
  const productName = getProductSelectorName(variant, unnamedProductLabel);
  const variantName = getProductVariantName(variant);
  return [productName, variantName].filter(Boolean).join(' — ');
};

/** Codes stay useful for lookup without leaking into the rendered option. */
export const buildProductSelectorSearchText = (variant: ProductVariant) => [
  variant.sku,
  variant.ean,
  variant.product?.ean,
  variant.product?.code,
  variant.product?.name,
  variant.product?.englishName,
  variant.product?.productCategoryName,
  variant.variantName,
  ...Object.entries(variant.attributes || {}).flatMap(([key, value]) => [key, value]),
  variant.width,
  variant.height,
  variant.length,
  variant.product?.width,
  variant.product?.height,
  variant.product?.length,
  getProductSelectorDimensions(variant),
].filter(Boolean).join(' ').toLowerCase();
